import React, { useState, useContext } from 'react';
import { CalendarContext } from '../../context/CalendarContext';
import { eachDayOfInterval, format, startOfDay, addHours } from 'date-fns';
import EventModal from '../Modals/EventModal';
import { getSlotLanguageCodes, LANG_BADGE } from '../../utils/languages';

const CalendarGrid = () => {
  const { startOfWeek, endOfWeek, isModalOpen, setIsModalOpen, availableHours } = useContext(CalendarContext);
  const [selectedEvent, setSelectedEvent] = useState({});
  
  // Obtiene los días de la semana
  const days = eachDayOfInterval({
    start: startOfWeek,
    end: endOfWeek,
  });

  // Genera un array con las horas de 5 am a 8 pm
  const hours = Array.from({ length: 14 }).map((_, index) =>
    format(addHours(startOfDay(new Date()), index + 8), 'HH:mm')
  );

  // Abre el modal y configura el evento seleccionado
  const handleEventClick = (date, hour, eventType) => {
    const cellDateKey = format(date, 'yyyy-MM-dd');
    const consultants = availableHours[cellDateKey]?.[eventType]?.find(entry => entry.hour === hour)?.consultants || [];
    setSelectedEvent({ date, hour, eventType, consultants });
    console.log({ date, hour, eventType, consultants })
    setIsModalOpen(true);
  };

  // Lugar flex (lista de espera de consultation days): no ocupa consultor.
  const handleFlexClick = (date, entry) => {
    setSelectedEvent({ date, hour: entry.hour, eventType: entry.type, consultants: entry.consultants, flex: true });
    setIsModalOpen(true);
  };

  // Cierra el modal y limpia el evento seleccionado
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent({});
  };

  // Obtener la disponibilidad de los consultores para cada celda
  const getConsultantsCountForDayByType = (date, hour, type) => {
    const key = format(date, 'yyyy-MM-dd');
    const dayData = availableHours[key];

    if (!dayData || !dayData[type]) return 0;

    const matchingEntry = dayData[type].find(entry => entry.hour === hour);

    if (!matchingEntry) return 0;

    // Consultation days con tope por horario: el backend manda los cupos que quedan.
    return matchingEntry.remaining ?? matchingEntry.consultants?.length ?? 0;
  };

  // Idiomas disponibles (badges EN/ES) para un slot, según sus consultores.
  const getLanguageBadgesForSlot = (date, hour, type) => {
    const key = format(date, 'yyyy-MM-dd');
    const matchingEntry = availableHours[key]?.[type]?.find(entry => entry.hour === hour);
    return getSlotLanguageCodes(matchingEntry?.consultants).map(code => LANG_BADGE[code]);
  };


  return (
    <>
      {isModalOpen && <EventModal eventDetails={selectedEvent} closeModal={handleCloseModal} />}
      <div className="calendar-grid">
        <div className="time-column">
          {hours.map(hour => <div key={hour} className="time-cell">{hour}</div>)}
        </div>
        <div className="days-row">
          {days.map(day => (
            <div key={day} className="day-column">
              {hours.map(hour => {
                const cellDateKey = format(day, 'yyyy-MM-dd');
                // Todas las entradas que arrancan dentro de esta hora: en días normales
                // hay una sola ('HH:00'); en consultation days hay varias (:00, :15, ...).
                const hourPrefix = hour.slice(0, 3);
                const getEntries = type =>
                  (availableHours[cellDateKey]?.[type] || []).filter(entry => entry.hour.startsWith(hourPrefix));
                const virtualEntries = getEntries('virtual');
                const presencialEntries = getEntries('presencial');
                // Lista de espera de consultation days (no ocupa consultor).
                const flexEntries = getEntries('flex');
                // Consultation day: algún slot del día no arranca en punto → chips compactos (hora + cantidad).
                const dayData = availableHours[cellDateKey] || {};
                const isStaggered = [...(dayData.virtual || []), ...(dayData.presencial || [])].some(entry => !entry.hour.endsWith(':00'));

                let className = 'event-cell';
                if (!virtualEntries.length && !presencialEntries.length && !flexEntries.length) {
                  className += ' empty';
                }
                if (isStaggered) {
                  className += ' staggered';
                }

                const renderChip = (entry, type) => (
                  <div
                    key={`${type}-${entry.hour}`}
                    onClick={() => handleEventClick(format(day, 'dd MMM yyyy'), entry.hour, type)}
                    className={`available ${type}`}
                  >
                    {isStaggered ? (
                      <>
                        <span className="consultants">{getConsultantsCountForDayByType(day, entry.hour, type)}</span>
                        <span>{entry.hour}</span>
                      </>
                    ) : (
                      <>
                        <span className="consultants">{getConsultantsCountForDayByType(day, entry.hour, type)}</span>
                        <span role="img" aria-label={type === 'virtual' ? 'Mango' : 'Pin'}>{type === 'virtual' ? '🧑‍💻' : '📍'}</span>
                        <span>{type === 'virtual' ? 'Virtual' : 'Presencial'}</span>
                        <span>{entry.hour}</span>
                      </>
                    )}
                    <span className="lang-badges">
                      {getLanguageBadgesForSlot(day, entry.hour, type).map(badge => (
                        <span key={badge} className="lang-badge">{badge}</span>
                      ))}
                    </span>
                  </div>
                );

                return (
                  <div key={`${cellDateKey}-${hour}`} className={className}>
                    {virtualEntries.map(entry => renderChip(entry, 'virtual'))}
                    {presencialEntries.map(entry => renderChip(entry, 'presencial'))}
                    {flexEntries.map(entry => (
                      <div
                        key={`flex-${entry.hour}`}
                        onClick={() => handleFlexClick(format(day, 'dd MMM yyyy'), entry)}
                        className="available flex"
                        title="Lista de espera: entra si se libera un lugar"
                      >
                        <span>Flex</span>
                        <span>{entry.hour}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CalendarGrid;
